import { useLocation, useNavigate } from 'react-router';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useTranslation } from 'react-i18next';

import { SPECIAL_AGENT_IDS, useAgentVisibility } from '@/entities/agent';
import { getCurrentOrganizationId, useOrganizationApps, usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ROUTES } from '@/shared/config/routes';
import { useContentStudioAllowed } from '@/shared/config/useContentStudioAllowed';
import { useIqAllowed } from '@/shared/config/useIqAllowed';
import { useManagedToolAccess } from '@/shared/config/useManagedToolAccess';
import { useReelsCreatorAllowed } from '@/shared/config/useReelsCreatorAllowed';
import { openAdaptationPicker } from '@/shared/hooks';
import { ADAPTATION_AGENT_ID } from '@/shared/utils/adaptation';
import { CREATIVE_FLOWS_APP_ID, openCreativeFlowsTool } from '@/shared/utils/creative-flows';
import { FLOW_DIGEST_AGENT_ID, openFlowDigestTool } from '@/shared/utils/flow-digest';
import {
  openReelsCreatorTool,
  REELS_CREATOR_APP_SLUG,
  REELS_CREATOR_HOSTED_TOOL_ID,
  REELS_CREATOR_TOOL_ID,
} from '@/shared/utils/reels-creator';
import {
  OPPORTUNITIES_ROOM_AGENT_ID,
  openOpportunitiesRoom,
} from '@/shared/utils/opportunities-room';
import { withFocusGroupsOrigin } from '@/shared/utils/focusGroupsNavigation';

import type {
  ManagedTool,
  UseManagedToolsOptions,
  UseManagedToolsResult,
} from './useManagedTools.types';

/**
 * The tools the platform ships itself, rather than the ones an organization
 * registers as agents. Each is gated by some mix of feature flag, role
 * permission, and per-workspace agent visibility, and each opens a surface of
 * its own instead of a chat.
 *
 * The list is returned whole, across every category, in the order the grids
 * render it. Callers showing one category filter it themselves — which is also
 * what lets the tools catalog show every category side by side.
 */
export const useManagedTools = ({
  showFlowDigest = false,
  workspaceSlug,
}: UseManagedToolsOptions = {}): UseManagedToolsResult => {
  const { t } = useTranslation('springboard');
  const { t: tTools } = useTranslation('tools');
  const { t: tKnowledge } = useTranslation('knowledge');

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const workspaceId = useCurrentWorkspaceId();
  const organizationId = getCurrentOrganizationId();

  const { isAgentEnabled, isLoading: isVisibilityLoading } = useAgentVisibility(
    [SPECIAL_AGENT_IDS.DEEP_RESEARCH_OPPORTUNITY_IDENTIFIER],
    { workspaceId, organizationId },
  );
  const { canOpenTool, canViewTaxonomy } = usePermissions();
  const { canOpen: canOpenManagedTool, isLoading: isAccessLoading } = useManagedToolAccess();

  const { isAllowed: isIqAllowed, isLoading: isIqLoading } = useIqAllowed();
  const { isAllowed: isContentStudioAllowed, isLoading: isContentStudioLoading } =
    useContentStudioAllowed();
  const { isAllowed: isReelsCreatorAllowed, isLoading: isReelsCreatorLoading } =
    useReelsCreatorAllowed();
  const { data: organizationApps, isLoading: isAppsLoading } = useOrganizationApps();
  const reelsCreatorApp = organizationApps?.find((app) => app.slug === REELS_CREATOR_APP_SLUG);
  const isCreativeFlowsEnabled = useBooleanFlagValue('creative_flows_app', false);
  const isNACBIntakeEnabled = useBooleanFlagValue('nacb_trigger_intake', false);
  const isTaxonomyEnabled = useBooleanFlagValue('knowledge_hub_taxonomy', false);

  const tools: ManagedTool[] = [];

  if (showFlowDigest && workspaceId && canOpenTool(FLOW_DIGEST_AGENT_ID)) {
    tools.push({
      id: FLOW_DIGEST_AGENT_ID,
      category: 'plan',
      kind: 'app',
      title: tTools('flowDigestAgent.name'),
      description: t('springboard.toolGridV4.digestFlowAnalysisAndReporting'),
      onOpen: () => openFlowDigestTool(workspaceId),
    });
  }

  if (!!workspaceId && isAgentEnabled(SPECIAL_AGENT_IDS.DEEP_RESEARCH_OPPORTUNITY_IDENTIFIER)) {
    tools.push({
      id: OPPORTUNITIES_ROOM_AGENT_ID,
      category: 'plan',
      kind: 'app',
      title: tTools('toolsHub.toolsHubConstants.opportunitiesRoom'),
      description: t('springboard.toolGridV4.spotTimelyBrandSafeOpportunities'),
      onOpen: () => openOpportunitiesRoom(navigate),
    });
  }

  if (isContentStudioAllowed && !!workspaceId) {
    tools.push({
      id: 'content-studio',
      category: 'create',
      kind: 'app',
      title: tTools('toolsHub.toolsHubConstants.contentStudio'),
      description: tTools('toolsHub.toolsHubConstants.importDesignSystemsAndGenerateAds'),
      onOpen: () => void navigate(ROUTES.CONTENT_STUDIO),
    });
  }

  // Exactly one Reels Creator tile: the in-app tool where it is allowed, the hosted app
  // elsewhere. Neither shows while the gate resolves, or the hosted tile would flash and
  // then swap for the in-app one.
  if (!isReelsCreatorLoading && !!workspaceId) {
    if (isReelsCreatorAllowed) {
      tools.push({
        id: REELS_CREATOR_TOOL_ID,
        category: 'create',
        kind: 'app',
        title: tTools('managedTools.reelsCreator.name'),
        description: tTools('managedTools.reelsCreator.description'),
        onOpen: () => void navigate(ROUTES.REELS_CREATOR),
      });
    } else if (reelsCreatorApp) {
      tools.push({
        id: REELS_CREATOR_HOSTED_TOOL_ID,
        category: 'create',
        kind: 'app',
        title: tTools('managedTools.reelsCreator.name'),
        description: tTools('managedTools.reelsCreator.description'),
        onOpen: () => openReelsCreatorTool(reelsCreatorApp.hostedUrl, workspaceId),
      });
    }
  }

  if (isCreativeFlowsEnabled && workspaceId) {
    tools.push({
      id: CREATIVE_FLOWS_APP_ID,
      category: 'create',
      kind: 'app',
      title: tTools('creativeFlowsApp.name'),
      description: tTools('creativeFlowsApp.description'),
      onOpen: () => openCreativeFlowsTool(workspaceId),
    });
  }

  if (workspaceSlug) {
    tools.push({
      id: ADAPTATION_AGENT_ID,
      category: 'scale',
      kind: 'app',
      title: tTools('toolsHub.toolsHubConstants.adaptation'),
      description: t('springboard.toolGridV4.scaleVariationsAcrossMarkets'),
      onOpen: () => openAdaptationPicker(),
    });
  }

  // Generation is a `plan` activity: it produces the names a campaign is set up
  // with. Gated on the module flag AND the taxonomy view permission, the same
  // AND-gate the Knowledge Hub card uses.
  if (isTaxonomyEnabled && canViewTaxonomy) {
    tools.push({
      id: 'taxonomy-generation',
      category: 'plan',
      kind: 'app',
      title: tKnowledge('taxonomy.generationList.title'),
      description: tKnowledge('taxonomy.generationList.tileDescription'),
      onOpen: () => void navigate(ROUTES.TOOLS_TAXONOMY_GENERATION),
    });
  }

  if (!!workspaceId && canOpenManagedTool('test-content')) {
    tools.push({
      id: 'content-test',
      category: 'plan',
      kind: 'app',
      title: tTools('managedTools.contentTest.name'),
      description: tTools('managedTools.contentTest.description'),
      onOpen: () => void navigate(ROUTES.TOOLS_CONTENT_TEST),
    });
  }

  if (isNACBIntakeEnabled && !!workspaceId && canOpenManagedTool('nacb-intake')) {
    tools.push({
      id: 'nacb-intake',
      category: 'plan',
      kind: 'app',
      title: tTools('managedTools.nacbIntake.name'),
      description: tTools('managedTools.nacbIntake.description'),
      onOpen: () => void navigate(ROUTES.TOOLS_NACB_INTAKE),
    });
  }

  if (!!workspaceId && canOpenManagedTool('focus-groups')) {
    tools.push({
      id: 'focus-groups',
      category: 'plan',
      kind: 'app',
      title: tTools('managedTools.focusGroups.name'),
      description: tTools('managedTools.focusGroups.description'),
      // Record where the user launched from so the Focus Groups back button can
      // return them to the Tools homepage when they came from a Tools page.
      onOpen: () => void navigate(ROUTES.TOOLS_FOCUS_GROUPS, withFocusGroupsOrigin(pathname)),
    });
  }

  if (isIqAllowed) {
    tools.push({
      id: 'iq',
      category: 'plan',
      kind: 'app',
      title: tTools('managedTools.iq.name'),
      description: tTools('managedTools.iq.description'),
      onOpen: () => void navigate(ROUTES.IQ),
    });
  }

  // A visibility-gated tool reads as disabled while its query is in flight, so a
  // caller that does not wait on this renders a grid those tools pop into.
  return {
    tools,
    isLoading:
      isVisibilityLoading ||
      isAccessLoading ||
      isIqLoading ||
      isContentStudioLoading ||
      isReelsCreatorLoading ||
      isAppsLoading,
  };
};
